import { printDebug } from '@/lib/printDebug'
import { isBhooklyNativeServerApp, nativePrintJobs } from '@/lib/bhooklyNative'
import { CHAR_DOTS, headDotsForPaper, textBoxForPaper } from '@/lib/escpos/bytes'
import {
  awaitPrintResult,
  installPrintResultChannel,
  nextPrintBatchId,
  type PrintBatchResult,
} from '@/lib/printResult'

/**
 * Bhookly Android print — native WebView bridge (preferred) or Chrome Intent.
 *
 * Multi-printer: one call carries `{ v:1, jobs:[{ station, target, text }] }`.
 *
 * IMPORTANT: only call from Android (PWA / Server APK). On a desktop browser,
 * intent:// is a no-op (or navigates the tab) and must not replace window.print().
 */

const ANDROID_PACKAGE = 'demo.bhookly.com'

/** True when KOT print should go through the Android print agent (not HTML). */
export function isAndroidPrintClient(): boolean {
  if (typeof window === 'undefined') return false
  if (isBhooklyNativeServerApp()) return true
  try {
    return /Android/i.test(navigator.userAgent || '')
  } catch {
    return false
  }
}

export interface BridgePrintJob {
  station?: string
  target?: string
  text: string
  /**
   * Pre-built ESC/POS for this job (F-FE-N111).
   *
   * An agent from 1.3.0 on takes these bytes and never reads `text`; anything
   * older does not know the field and prints `text` exactly as it does today.
   * That is the whole safety story for moving KOTs onto this path — callers
   * MUST keep sending a real `text`, never a placeholder, so an un-updated
   * tablet degrades to the current behaviour instead of losing the ticket.
   */
  escpos?: Uint8Array
}

/**
 * What an agent too old to understand `escpos` prints instead of the receipt.
 *
 * Deliberately short and in the operator's language: whoever is holding the
 * tablet needs to know the sale is fine and what to do, not what a job field is.
 */
const STALE_AGENT_NOTICE = [
  '*** RECEIPT NOT PRINTED ***',
  '',
  'This tablet needs the newer',
  'Bhookly Android app (1.3.0+)',
  'to print receipts.',
  '',
  'The sale IS saved. Reprint it',
  'from the order, or print at',
  'the counter.',
].join('\n')

/** Uint8Array → base64, chunked so a long receipt cannot blow the call stack. */
function bytesToBase64(b: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < b.length; i += chunk) {
    binary += String.fromCharCode(...b.subarray(i, i + chunk))
  }
  return btoa(binary)
}

/**
 * Send pre-built ESC/POS bytes to one printer (F-FE-N105).
 *
 * The text path is not usable for a receipt: the agent wraps `text` with
 * `textToEscPos`, which hardcodes KITCHEN styling — emphasized + double-height
 * (`ESC ! 0x18`) — and appends its own cut. A receipt printed through it comes
 * out in bold double-height throughout. This path hands the agent finished
 * bytes and it writes them verbatim, so the caller owns every command,
 * including the raster QR that makes a fiscal invoice legal.
 *
 * Native shell only. The `intent://` fallback caps out around a URL's worth of
 * data and a receipt with a QR is far past it, so a PWA gets false and the
 * caller reports honestly rather than truncating a tax invoice.
 */
export function bridgePrintEscpos(bytes: Uint8Array, target?: string, station = 'Receipt'): boolean {
  return sendEscpos(bytes, target, station).ok
}

/**
 * Same handoff, but resolves with what the printer actually did (F-AND-N1).
 *
 * Returns null when the job was never handed over at all — a desktop browser,
 * a PWA, or empty bytes — which is a different thing from "handed over and
 * failed" and the caller must not conflate them.
 *
 * On an agent below 1.4.0 this resolves to UNKNOWN_RESULT, so a caller that
 * only acts on `failed > 0` behaves exactly as it does today on every tablet
 * still in the field.
 */
export function bridgePrintEscposAsync(
  bytes: Uint8Array,
  target?: string,
  station = 'Receipt',
): Promise<PrintBatchResult> | null {
  const { ok, id } = sendEscpos(bytes, target, station)
  if (!ok) return null
  return awaitPrintResult(id)
}

function sendEscpos(
  bytes: Uint8Array,
  target: string | undefined,
  station: string,
): { ok: boolean; id: string } {
  if (bytes.length === 0) return { ok: false, id: '' }
  if (!isBhooklyNativeServerApp()) {
    printDebug.warn('bridge', 'ESC/POS print needs the native shell — not available here')
    return { ok: false, id: '' }
  }
  // Install before sending: the agent can call back the instant it finishes,
  // and on a reachable printer that is faster than the next microtask.
  installPrintResultChannel()
  const id = nextPrintBatchId()
  const doc = {
    v: 1 as const,
    id,
    jobs: [
      {
        station,
        target: normalizeAgentTarget(target),
        // F-FE-N106: `text` carries an UPGRADE NOTICE, not an empty string.
        //
        // It was empty at first, reasoning that an older agent would ignore a
        // job with no text rather than print the base64 as if it were the
        // receipt. That was the wrong trade. An agent below 1.3.0 does
        // `if (text.isBlank()) continue` — so it skipped the job in silence,
        // and the cashier tapped Print and watched nothing happen. That is
        // precisely the failure F-FE-N104 existed to remove, reintroduced one
        // layer down and only on the tablets that had not updated yet.
        //
        // An agent that understands `escpos` never renders this: it takes the
        // bytes and returns before reading `text`. So the only device that
        // prints this slip is one that could not have printed the receipt
        // anyway, and it now says why instead of failing mutely.
        text: STALE_AGENT_NOTICE,
        escpos: bytesToBase64(bytes),
      },
    ],
  }
  printDebug.log('bridge', 'native ESC/POS job', {
    bytes: bytes.length,
    target: doc.jobs[0].target || 'default',
  })
  return { ok: nativePrintJobs(JSON.stringify(doc)), id }
}

export function normalizeAgentTarget(raw: string | undefined | null): string {
  const t = (raw ?? '').trim()
  if (!t || /\s/.test(t)) return ''
  if (t.includes(':')) return t
  return `${t}:9100`
}

/**
 * Does this `printer_target` name a NETWORK printer rather than a Windows one?
 *
 * `kitchen_stations.printer_target` is one VARCHAR holding two incompatible
 * kinds of value with no discriminator — a LAN `ip:port` for the Android agent,
 * or an OS printer name for the desktop app — and the correct reading depends
 * entirely on which device is printing. The desktop hands it straight to
 * SumatraPDF as a device name, so an `ip:port` there fails every job.
 *
 * That is not hypothetical. Mizaj run a tablet as the till and fall back to a
 * PC when the tablet is down, which is exactly when they can least afford the
 * kitchen tickets to stop. Setting their station's target to the tablet's
 * address — the obvious thing to do, so the routing survives a tablet wipe —
 * would have broken that fallback silently.
 *
 * printAgent.ts already refuses the column outright for relayed jobs, for this
 * same reason. This is the narrower version: ignore it only when it is
 * unmistakably an address. Deliberately conservative — anything that does not
 * match is treated as a printer name, which is today's behaviour.
 */
export function looksLikeNetworkTarget(raw: string | undefined | null): boolean {
  const t = (raw ?? '').trim()
  if (!t || /\s/.test(t)) return false
  // Bare IPv4, with or without a port.
  if (/^\d{1,3}(\.\d{1,3}){3}(:\d{1,5})?$/.test(t)) return true
  // host:port — a Windows printer name essentially never ends in `:digits`.
  return /^[A-Za-z0-9._-]+:\d{1,5}$/.test(t)
}

function utf8ToBase64(s: string): string {
  const bytes = new TextEncoder().encode(s)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function buildIntentUrl(base64: string): string {
  return (
    `intent://print#Intent;` +
    `scheme=bhooklyprint;` +
    `package=${ANDROID_PACKAGE};` +
    `S.escpos=${encodeURIComponent(base64)};` +
    `end`
  )
}

function launchBhooklyPrintIntent(base64: string): boolean {
  if (typeof window === 'undefined') return false
  const uri = buildIntentUrl(base64)
  printDebug.log('bridge', 'launching Android intent', { uriLength: uri.length })

  try {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('style', 'display:none;width:0;height:0;border:0;position:absolute')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.src = uri
    document.body.appendChild(iframe)
    window.setTimeout(() => {
      try {
        document.body.removeChild(iframe)
      } catch {
        /* already gone */
      }
    }, 2500)
  } catch (err) {
    printDebug.warn('bridge', 'iframe intent launch failed', err)
  }

  try {
    window.location.href = uri
    return true
  } catch (err) {
    printDebug.error('bridge', 'intent href assignment threw', err)
    return false
  }
}

/**
 * Print one or more KOT jobs via native bridge (Server APK) or Chrome Intent (PWA).
 */
export function bridgePrintJobs(jobs: BridgePrintJob[]): boolean {
  return sendJobs(jobs).ok
}

/**
 * Same as bridgePrintJobs, but resolves with what each station's printer did.
 * Returns null when nothing was handed over. See bridgePrintEscposAsync.
 */
export function bridgePrintJobsAsync(jobs: BridgePrintJob[]): Promise<PrintBatchResult> | null {
  const { ok, id } = sendJobs(jobs)
  if (!ok) return null
  return awaitPrintResult(id)
}

function sendJobs(jobs: BridgePrintJob[]): { ok: boolean; id: string } {
  const clean = jobs
    .map((j) => ({
      station: (j.station ?? '').trim(),
      target: normalizeAgentTarget(j.target),
      text: (j.text ?? '').toString(),
      escpos: j.escpos && j.escpos.length > 0 ? j.escpos : undefined,
    }))
    // Still keyed on `text`: a job with no text is not printable by an older
    // agent, and this path must never depend on the newer one.
    .filter((j) => j.text.trim().length > 0)

  if (clean.length === 0) {
    printDebug.log('bridge', 'no job text — nothing to print')
    return { ok: false, id: '' }
  }

  // Install before sending: the agent can call back the instant it finishes,
  // and on a reachable printer that is faster than the next microtask.
  installPrintResultChannel()
  const id = nextPrintBatchId()
  const doc = {
    v: 1 as const,
    id,
    jobs: clean.map((j) => ({
      station: j.station,
      target: j.target,
      text: j.text,
      ...(j.escpos ? { escpos: bytesToBase64(j.escpos) } : {}),
    })),
  }
  const json = JSON.stringify(doc)

  // Native Server WebView: NEVER fall through to intent:// — the WebView used
  // to block those navigations ("Blocked navigation outside …") and no paper
  // came out. Bridge-only path here.
  if (isBhooklyNativeServerApp()) {
    printDebug.log('bridge', `native printJobs (${clean.length})`, {
      stations: clean.map((j) => j.station || '(unnamed)'),
      targets: clean.map((j) => j.target || 'default'),
    })
    if (nativePrintJobs(json)) return { ok: true, id }
    printDebug.warn('bridge', 'native printJobs unavailable — not falling back to intent in WebView')
    return { ok: false, id: '' }
  }

  // The intent:// fallback caps out around a URL's worth of data, so the PWA
  // path sends text only — it is the older, narrower transport and the bytes
  // are an enhancement, not a requirement.
  const base64 = utf8ToBase64(
    JSON.stringify({ v: 1 as const, jobs: clean.map(({ escpos: _drop, ...j }) => j) }),
  )
  printDebug.log('bridge', `firing multi-job intent (${clean.length})`, {
    base64Length: base64.length,
  })
  // The intent path has no way back to this page, so the id is dead weight
  // here. awaitPrintResult resolves it as unknown, which is honest.
  return { ok: launchBhooklyPrintIntent(base64), id }
}

export function bridgePrintSlips(slips: string[]): boolean {
  return bridgePrintJobs(slips.map((text) => ({ text })))
}

/**
 * Alignment card (F-FE-N113).
 *
 * Two things sit behind "the slip prints left of centre", and a photograph
 * cannot separate them: the printer may be ignoring our `GS L`, or the roll may
 * be seated off centre under a perfectly placed image. This prints the evidence.
 *
 * Read it on the paper:
 *   - is the FULL bar clipped at either edge?  -> the roll is off centre, and by
 *     how much; set the print offset to match (Admin -> the receipt printer field)
 *   - is the INSET bar visibly narrower and shifted right of the full one?
 *     -> the printer honours GS L
 *   - are both bars identical? -> GS L is being ignored, and only the offset
 *     (which prints literal spaces) will move anything
 */
export function bridgePrintAlignmentCard(paperWidthMm = 80, offsetCols = 0): boolean {
  const head = headDotsForPaper(paperWidthMm)
  const full = Math.floor(head / CHAR_DOTS)
  const box = textBoxForPaper(paperWidthMm)
  const ruler = '....|....'.repeat(6).slice(0, full)
  const slip = [
    '*** ALIGNMENT CARD ***',
    '',
    `FULL ${full} cols, no margin:`,
    '#'.repeat(full),
    '',
    `INSET ${box.columns} cols, GS L ${box.leftDots} dots:`,
    '#'.repeat(box.columns),
    '',
    `OFFSET ${offsetCols} cols (literal spaces):`,
    ' '.repeat(offsetCols) + '#'.repeat(Math.max(1, box.columns - offsetCols)),
    '',
    ruler,
    '',
    'Both bars the same width and',
    'position => GS L ignored; use the',
    'offset. Full bar clipped => the',
    'roll is seated off centre.',
  ].join('\n')
  return bridgePrintJobs([{ station: 'Align', target: '', text: slip }])
}

export function bridgePrintTest(): boolean {
  const now = new Date()
  const slip = [
    '   *** BHOOKLY AGENT TEST ***',
    '',
    'POS → print agent → default printer',
    now.toLocaleString(),
    '',
    'Per-station KOTs use Admin → Stations',
    'printer target (ip or ip:port).',
  ].join('\n')
  return bridgePrintJobs([{ station: 'Test', target: '', text: slip }])
}

/**
 * Prove ONE configured address, and say what happened (F-AND-N1).
 *
 * bridgePrintTest above only ever reaches the agent's own default IP, and says
 * so on screen. So there has been no way at all to test the address in the
 * receipt-printer box or a station's target — which is exactly what Mizaj
 * needed on 2026-09-10 and could not do
 * (audit/MIZAJ_TABLET_PRINT_ECONNREFUSED_2026-09-10.md).
 *
 * Resolves to null when the job never left the page. On an agent below 1.4.0
 * the slip is still sent; the result is simply unknown, and the caller should
 * say "sent" rather than claim success.
 */
export function bridgePrintTestTo(
  target: string,
  label = 'Test',
): Promise<PrintBatchResult> | null {
  const normalized = normalizeAgentTarget(target)
  const now = new Date()
  const slip = [
    '  *** BHOOKLY PRINTER TEST ***',
    '',
    `Target: ${normalized || '(agent default)'}`,
    `For:    ${label}`,
    now.toLocaleString(),
    '',
    'If this slip printed, the address',
    'above is correct and reachable',
    'from this device.',
  ].join('\n')
  return bridgePrintJobsAsync([{ station: label, target: normalized, text: slip }])
}
